import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, LineReveal, SectionLabel } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Faq from "@/components/Faq";
import Causes from "@/components/Causes";
import WhyChoose from "@/components/WhyChoose";
import Parcours from "@/components/Parcours";
import Approches from "@/components/Approches";
import { SERVICE_CATEGORIES, TESTIMONIALS, IMAGES, SIGNATURE } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 md:pt-40">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24">
        <div className="grid items-end gap-14 pb-20 md:pb-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-sage-dark"
            >
              <span className="h-px w-10 bg-sage" aria-hidden="true" />
              Thérapie • Bien-être • Spiritualité
            </motion.p>

            <LineReveal
              lines={["ÉMERGENCE", "PRUDHOMME"]}
              className="mt-8 font-serif text-[13vw] font-light leading-[0.92] tracking-tight text-deep sm:text-[9.5vw] lg:text-[6.2vw]"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 max-w-xl font-serif text-2xl italic leading-snug text-deep/75 md:text-3xl"
            >
              Un espace pour mieux se comprendre, retrouver du sens et avancer.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/rendez-vous"
                data-testid="hero-cta-rendezvous"
                className="group inline-flex items-center gap-3 rounded-full bg-deep px-8 py-4 text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-gold hover:text-deep"
              >
                Prendre rendez-vous
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="inline-flex items-center gap-3 rounded-full border border-deep/20 px-8 py-4 text-xs uppercase tracking-[0.2em] text-deep transition-colors duration-300 hover:border-gold hover:text-gold"
              >
                Découvrir les services
              </Link>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -left-4 -top-4 h-full w-full rounded-t-full border border-gold/50" aria-hidden="true" />
              <div className="relative aspect-[3/4] overflow-hidden rounded-t-full">
                <motion.img
                  src={IMAGES.hero}
                  alt="Femme sereine face à la mer, dans une lumière naturelle"
                  data-testid="hero-image"
                  loading="eager"
                  fetchPriority="high"
                  style={{ y: imgY }}
                  className="h-[115%] w-full scale-110 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 rounded-2xl bg-deep px-7 py-5 text-cream shadow-xl md:-left-12">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Centre Émergence Prudhomme</p>
                <p className="mt-1 font-serif text-lg italic leading-snug">
                  Un espace pour faire émerger<br />le meilleur de vous-même
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-36 lg:px-24">
    <div className="grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal>
          <SectionLabel>Bienvenue</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl font-light leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
            Vous traversez une période <em className="text-sage-dark">difficile</em>, de <em className="text-sage-dark">questionnement</em> ou de <em className="text-sage-dark">transition</em> ?
          </h2>
          <p className="mt-8 text-lg leading-relaxed text-deep/70">
            Vous ressentez le besoin d'être écouté, accompagné et soutenu pour mieux comprendre ce que vous vivez, trouver des solutions adaptées et avancer vers un mieux-être durable ?
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal delay={0.15}>
          <div className="overflow-hidden rounded-3xl">
            <img
              loading="lazy"
              decoding="async"
              src="/images/photo-ecoute-accueil.webp"
              alt="Femme noire sereine assise dans un fauteuil en lin, baignée d'une douce lumière — un moment d'écoute et d'accompagnement"
              data-testid="intro-photo"
              className="aspect-[4/5] w-full object-cover object-top transition-transform duration-[2000ms] hover:scale-105"
            />
          </div>
          <p className="mt-10 text-lg leading-relaxed text-deep/70">
            Au Centre Émergence Prudhomme, je vous accompagne dans un espace d'écoute, de réflexion et de croissance où nous pouvons explorer ensemble votre vécu, vos émotions, votre identité, vos aspirations, votre vocation et votre dimension spirituelle.
          </p>
          <p className="mt-6 border-l-2 border-gold pl-6 font-serif text-xl italic leading-relaxed text-deep/80">
            Pour les personnes qui le désirent, la démarche peut également intégrer la foi chrétienne, les enseignements bibliques, la prière et la relation avec Jésus-Christ.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const GrowthBanner = () => (
  <section data-testid="growth-banner" className="mx-auto max-w-[1500px] px-6 pb-24 md:px-12 md:pb-36 lg:px-24">
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl">
        <img
          loading="lazy"
          decoding="async"
          src={IMAGES.growth}
          alt="Main arrosant délicatement une jeune pousse dans la terre"
          className="h-[50vh] w-full object-cover transition-transform duration-[2000ms] hover:scale-105 md:h-[65vh]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-deep/50 via-deep/10 to-transparent" aria-hidden="true" />
        <p className="absolute bottom-8 left-8 max-w-lg font-serif text-2xl font-light italic leading-snug text-cream md:bottom-12 md:left-12 md:text-4xl">
          « Grandir commence par une seule graine d'attention. »
        </p>
      </div>
    </Reveal>
  </section>
);

const ServicesPreview = () => (
  <section data-testid="services-preview-section" className="bg-sand/40 py-24 md:py-36">
    <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <h2 className="max-w-xl font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Nos services
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-preview-link"
            className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-deep/60 transition-colors hover:text-gold"
          >
            Tous les services
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-16">
        {SERVICE_CATEGORIES.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.05}>
            <Link
              to={s.link ? s.link.to : `/services#${s.slug}`}
              data-testid={`service-row-${s.slug}`}
              className="group grid items-center gap-4 border-t border-deep/10 py-8 transition-colors duration-500 last:border-b hover:bg-cream md:grid-cols-12 md:py-10"
            >
              <span className="font-serif text-5xl font-light text-gold/60 transition-colors duration-500 group-hover:text-gold md:col-span-2 md:text-6xl">
                {s.id}
              </span>
              <h3 className="font-serif text-3xl font-light tracking-tight text-deep md:col-span-4 md:text-4xl">
                {s.title}
              </h3>
              <p className="text-deep/60 md:col-span-5">{s.short}</p>
              <span className="hidden justify-self-end md:col-span-1 md:block">
                <ArrowRight className="h-6 w-6 text-deep/30 transition-all duration-500 group-hover:translate-x-2 group-hover:text-gold" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const AboutTeaser = () => (
  <section data-testid="about-teaser-section" className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-36 lg:px-24">
    <div className="grid items-center gap-14 lg:grid-cols-12">
      <div className="relative lg:col-span-5">
        <Reveal>
          <div className="absolute -right-4 -top-4 h-full w-full rounded-2xl border border-sage/40" aria-hidden="true" />
          <img
            loading="lazy"
            decoding="async"
            src={IMAGES.portrait}
            alt="Portrait chaleureux de Jane Prudhomme, thérapeute"
            data-testid="about-teaser-image"
            className="relative aspect-[4/5] w-full rounded-2xl object-cover object-top"
          />
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal>
          <SectionLabel>À propos</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">
            Un espace pour faire émerger le meilleur de vous-même
          </h2>
          <p className="mt-8 text-lg leading-relaxed text-deep/70">
            Je crois que chaque personne possède en elle des ressources, des forces et un potentiel de croissance qui peuvent être développés et mobilisés — même dans les périodes difficiles.
          </p>
          <p className="mt-6 text-lg leading-relaxed text-deep/70">
            Depuis plus de 15 ans, j'accompagne des personnes et des organisations dans leur développement, leur évolution et l'atteinte de leurs objectifs, à la croisée de la relation d'aide, du mieux-être au travail, du leadership et de la spiritualité.
          </p>
          <Link
            to="/a-propos"
            data-testid="about-teaser-link"
            className="group mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-deep transition-colors hover:text-gold"
          >
            Découvrir mon parcours
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % TESTIMONIALS.length), 10000);
    return () => clearInterval(t);
  }, []);

  const item = TESTIMONIALS[index];
  return (
    <section data-testid="testimonials-section" className="bg-deep py-24 text-cream md:py-36">
      <div className="mx-auto max-w-[1100px] px-6 md:px-12">
        <Reveal>
          <SectionLabel>Témoignages</SectionLabel>
        </Reveal>
        <div className="mt-12 min-h-[320px] md:min-h-[480px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <p data-testid="testimonial-quote" className={`font-serif font-light italic leading-snug ${item.quote.length > 350 ? "text-xl sm:text-2xl md:text-3xl" : "text-2xl sm:text-3xl md:text-4xl"}`}>
                « {item.quote} »
              </p>
              <footer className="mt-8 flex items-center gap-4">
                <span className="h-px w-12 bg-gold" aria-hidden="true" />
                <div>
                  <p data-testid="testimonial-author" className="text-sm uppercase tracking-[0.2em] text-gold">{item.author}</p>
                  <p className="mt-1 text-sm text-cream/50">{item.context}</p>
                </div>
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-10 flex items-center gap-4">
          <button
            data-testid="testimonial-prev-button"
            onClick={() => setIndex((index - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
            className="rounded-full border border-cream/20 p-3 transition-colors duration-300 hover:border-gold hover:text-gold"
            aria-label="Témoignage précédent"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            data-testid="testimonial-next-button"
            onClick={() => setIndex((index + 1) % TESTIMONIALS.length)}
            className="rounded-full border border-cream/20 p-3 transition-colors duration-300 hover:border-gold hover:text-gold"
            aria-label="Témoignage suivant"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="ml-4 flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-1.5 bg-cream/25"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => (
  <section data-testid="cta-band-section" className="mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-36 lg:px-24">
    <Reveal>
      <div className="relative overflow-hidden rounded-3xl bg-sage/15">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <div className="relative px-8 py-16 md:px-16 md:py-24 lg:col-span-7">
            <span className="pointer-events-none absolute -right-6 -top-14 font-serif text-[12rem] font-light italic leading-none text-gold/15" aria-hidden="true">
              &
            </span>
            <h2 className="relative max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              La démarche commence là où vous êtes.
            </h2>
            <p className="relative mt-6 max-w-xl text-lg text-deep/60">
              Vous n'avez pas besoin d'avoir toutes les réponses avant de commencer. Venez simplement avec une question, un désir de changement ou le besoin de faire le point.
            </p>
            <Link
              to="/rendez-vous"
              data-testid="cta-band-button"
              className="group relative mt-10 inline-flex items-center gap-3 rounded-full bg-deep px-8 py-4 text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-gold hover:text-deep"
            >
              Prendre rendez-vous
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="relative hidden px-8 pb-0 lg:col-span-5 lg:block lg:pr-16">
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-t-full border border-gold/60" aria-hidden="true" />
              <div className="relative aspect-[3/4] overflow-hidden rounded-t-full">
                <img
                  loading="lazy"
                  decoding="async"
                  src={IMAGES.freedom}
                  alt="Femme les bras grands ouverts dans un champ baigné de lumière dorée"
                  data-testid="cta-band-image"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Intro />
      <GrowthBanner />
      <ServicesPreview />
      <Parcours />
      <Approches />
      <WhyChoose />
      <Causes />
      <AboutTeaser />
      <Testimonials />
      <Faq />
      <CtaBand />
    </main>
  );
}
